/**
 * DocumentTemplatesPage Component
 *
 * Page for viewing and managing custom document templates.
 * Features:
 * - List all saved document templates
 * - View template details
 * - Delete templates
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useDeleteV2ProviderDocumentsPromptsByPromptIdMutation,
  useGetV2ProviderDocumentsPromptsQuery,
} from '@features/markdown-editor-ai/store/api';
import type { CustomPromptResponse } from '@features/markdown-editor-ai/types';
import {
  AccessTime as TimeIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { formatSnakeCase } from '@utils/utils';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

export const DocumentTemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { document: documentMetrics } = useEventTracking();

  // Track page view
  const hasTrackedPageView = useRef(false);

  // Fetch templates
  const { data, isLoading, refetch } = useGetV2ProviderDocumentsPromptsQuery();
  const [deleteTemplate] = useDeleteV2ProviderDocumentsPromptsByPromptIdMutation();

  const templates = data?.prompts || [];

  // Track page view when templates load
  useEffect(() => {
    if (!isLoading && !hasTrackedPageView.current) {
      documentMetrics.templateLibraryViewed({
        template_count: templates.length,
      });
      hasTrackedPageView.current = true;
    }
  }, [isLoading, templates.length, documentMetrics]);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<CustomPromptResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = useCallback((template: CustomPromptResponse) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!templateToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTemplate({ promptId: templateToDelete.prompt_id }).unwrap();
      // Track template deletion
      documentMetrics.templateDeleted({
        template_id: templateToDelete.prompt_id,
        template_name: templateToDelete.name,
      });
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete template:', error);
      enqueueSnackbar('Failed to delete template', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [templateToDelete, deleteTemplate, enqueueSnackbar, refetch, documentMetrics]);

  const handleCancelDelete = useCallback(() => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  }, [isDeleting]);

  return (
    <Box
      sx={{
        height: { xs: 'calc(100vh - 56px)', md: 'calc(100vh - 64px)' },
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Page Header */}
      <Box sx={{ mb: 3, flexShrink: 0 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <Tooltip title="Back to Other Docs">
            <IconButton onClick={() => navigate('/documents')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <DocumentIcon color="action" sx={{ fontSize: 32 }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                Document Templates
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Manage your saved document templates
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
            }}
          >
            <DocumentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Templates Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
              When you save a document prompt as a template, it will appear here. Templates let you
              quickly generate the same type of document again.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/documents')}>
              Back to Other Docs
            </Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {templates.map(template => (
              <Card
                key={template.prompt_id}
                variant="outlined"
                sx={{
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <CardContent>
                  <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{alignItems: "center",  mb: 1 }}>
                        <DocumentIcon color="primary" fontSize="small" />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          {template.name}
                        </Typography>
                      </Stack>

                      {template.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          {template.description}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                        <Chip
                          label={formatSnakeCase(template.document_type) || 'Document'}
                          size="small"
                          variant="outlined"
                        />
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                          <TimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.disabled">
                            Created {format(new Date(template.created_at), 'MMM d, yyyy')}
                          </Typography>
                        </Stack>
                        {template.updated_at && template.updated_at !== template.created_at && (
                          <Typography variant="caption" color="text.disabled">
                            · Updated {format(new Date(template.updated_at), 'MMM d, yyyy')}
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    <Tooltip title="Delete template">
                      <IconButton
                        onClick={() => handleDeleteClick(template)}
                        size="small"
                        sx={{
                          ml: 2,
                          '&:hover': {
                            bgcolor: 'error.light',
                            color: 'error.contrastText',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the template &quot;{templateToDelete?.name}&quot;? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentTemplatesPage;

